import {
  ChangeEvent,
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
} from 'react';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import classNames from 'classnames';

import { AppError } from '@vk-hr-tek/core/error';
import { HTTP_FORBIDDEN_STATUS } from '@vk-hr-tek/core/http';
import { useDebounce, useShadowRootElement } from '@vk-hr-tek/ui/hooks';
import { PortalContext } from '@vk-hr-tek/ui/Portal';
import { CircularProgress } from '@vk-hr-tek/ui/CircularProgress';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { IconButton } from '@vk-hr-tek/ui/IconButton';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { useInject } from '@vk-hr-tek/core/ioc';
import { t } from '@vk-hr-tek/core/translations';

import { UsersService } from '@app/gen/users';
import { ContentService } from '@app/gen/content';
import { useDispatch, useSelector } from '@app/hooks';

import {
  search,
  selectSearchStatus,
  selectSearchItems,
  getContentSearchGroups,
  selectSearchGroups,
  selectSearchError,
} from '../../../slice';

import { ListBox } from './ListBox';
import {
  SearchInputProps,
  ContentSearchOptions,
  LoadItemsFilter,
  ImageParams,
  PhotoParams,
} from './SearchInput.types';
import { GroupFilter } from './GroupFilter';
import { useStyles } from './SearchInput.styles';
import { SearchOption } from './SearchOption';

const DEBOUNCE_TIMEOUT = 200;
const DEFAULT_WIDTH_SIZE = 224;

export const SearchInput = ({
  disabled = false,
  closedStateComponent,
  minInputValueLength = 2,
  testId,
  onInputValueChange,
}: SearchInputProps) => {
  const [open, setOpen] = useState(false);
  const userService = useInject(UsersService);
  const contentApiService = useInject(ContentService);
  const dispatch = useDispatch();

  const width = open ? '100%' : DEFAULT_WIDTH_SIZE;

  const { classes } = useStyles({ width });

  const portalContext = useContext(PortalContext);
  const popperContainer = useShadowRootElement(
    portalContext?.portalId
      ? `portal-container-${portalContext.portalId}`
      : undefined,
  );

  const inputRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const [showGroupFilter, setShowGroupFilter] = useState(false);

  const status = useSelector(selectSearchStatus);
  const items = useSelector(selectSearchItems);
  const categories = useSelector(selectSearchGroups);
  const error = useSelector(selectSearchError);

  const [inputValue, setInputValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<ContentSearchOptions | null>(
    null,
  );
  const [filter, setFilter] = useState<LoadItemsFilter>({
    query: '',
    group: undefined,
  });
  const isDropdownOpenRef = useRef(false);

  const loadPhoto = useCallback(
    async ({ id }: PhotoParams): Promise<Blob> => {
      const response = await userService.photo({ params: { user_id: id } });

      return response.file;
    },
    [userService],
  );

  const loadImage = useCallback(
    async ({ id }: ImageParams): Promise<string> => {
      const response = await contentApiService.getAttachmentUrl({
        params: { attachment_id: id },
      });

      return response.url;
    },
    [contentApiService],
  );

  const debouncedInputValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);
  const showResult = debouncedInputValue.length >= minInputValueLength;

  const autocompleteOpenHandler = () => {
    setOpen(true);
    setShowGroupFilter(true);

    if (containerRef.current) {
      const iconWidth = 38;
      setDropdownWidth(containerRef.current.offsetWidth + iconWidth);
    }
  };
  const autocompleteBlurHandler = (event: React.FocusEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    if (relatedTarget && containerRef.current?.contains(relatedTarget)) {
      return;
    }

    if (
      relatedTarget?.closest('.MuiAutocomplete-popper') ||
      relatedTarget?.closest('[role="menu"]') ||
      relatedTarget?.closest('.MuiMenu-paper')
    ) {
      return;
    }

    if (isDropdownOpenRef.current) {
      return;
    }

    setOpen(false);
    setShowGroupFilter(false);
  };
  const autocompleteFocusHandler = () => {
    if (isDropdownOpenRef.current) {
      return;
    }
    setInputValue('');
  };

  const autocompleteResetHandler = () => {
    setInputValue('');
  };
  const autocompleteClouseHandler = () => {
    setInputValue('');
    setOpen(false);
    setShowGroupFilter(false);
  };
  const autocompleteGetInputValue = () => {
    if (!open && !inputValue) {
      return (selectedItem?.type || '').toString().split('\n').join(', ');
    }

    return inputValue;
  };

  const autocompleteInputChangeHandler = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: ChangeEvent<Record<string, any>>,
    nextValue: string,
    reason: string,
  ) => {
    if (
      (reason === 'blur' || reason === 'reset') &&
      isDropdownOpenRef.current
    ) {
      return;
    }

    if (reason === 'reset') {
      setInputValue('');
      setFilter({ ...filter, query: '' });

      return;
    }

    if (reason === 'clear') {
      setSelectedItem(null);
    }

    if (reason === 'input') {
      setFilter({ ...filter, query: nextValue });
    }

    if (onInputValueChange) {
      onInputValueChange(nextValue);
    }

    setInputValue(nextValue);
  };

  useEffect(() => {
    setFilter({
      query: '',
      group: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    const handleTabSwitch = () => inputRef.current?.blur();

    document.addEventListener('visibilitychange', handleTabSwitch);

    return () => {
      document.removeEventListener('visibilitychange', handleTabSwitch);
    };
  }, []);

  useEffect(() => {
    if (!inputRef.current || !open) {
      return;
    }

    const iconWidth = 38;

    const ro = new ResizeObserver(() => {
      const offsetWidth = inputRef.current?.offsetWidth;

      if (offsetWidth) {
        setDropdownWidth(offsetWidth + iconWidth);
        setShowGroupFilter(true);
      }
    });

    ro.observe(inputRef.current);

    return () => {
      ro.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !showResult) {
      return;
    }

    dispatch(
      search({
        query: debouncedInputValue,
        group: filter.group,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue, filter.group]);

  useEffect(() => {
    if (!open) {
      autocompleteResetHandler();
    }
  }, [open]);

  useEffect(() => {
    dispatch(getContentSearchGroups());
  }, [dispatch]);

  useEffect(() => {
    if (categories.length === 1) {
      setFilter((prev) => ({
        ...prev,
        group: categories[0].value as LoadItemsFilter['group'],
      }));
    }
  }, [categories]);

  const getErrorText = (searchError: AppError | null) => {
    if (searchError?.status === HTTP_FORBIDDEN_STATUS) {
      return 'Поиск по разделу не выполнен. Попробуйте ещё раз';
    }

    return 'Выполнение данной операции на текущий момент недоступно';
  };

  const handleDropdownOpen = useCallback(() => {
    isDropdownOpenRef.current = true;
  }, []);

  const handleDropdownClose = useCallback(() => {
    inputRef.current?.focus();
    requestAnimationFrame(() => {
      isDropdownOpenRef.current = false;
    });
  }, []);

  const customPaperProps = {
    filter,
    setFilter,
    categories,
    show: showGroupFilter,
    showResult,
    onClose: autocompleteClouseHandler,
    onDropdownOpen: handleDropdownOpen,
    onDropdownClose: handleDropdownClose,
    dropdownWidth,
  };
  const customListboxProps = {
    filter,
    onClose: autocompleteClouseHandler,
  };

  const getPlaceholder = () => {
    const group = open ? filter.group : undefined;

    const placeholderKey = group
      ? `search.fullSearch.placeholders.${group}`
      : 'search.title';

    return t(placeholderKey);
  };

  const searchComponent = (
    <Box className={classNames('aqa_autocomplete_input', classes.root)}>
      <Autocomplete
        ref={containerRef}
        blurOnSelect
        openOnFocus
        data-testid={testId}
        classes={{
          paper: classes.list,
          option: classes.option,
          listbox: classes.listBox,
        }}
        open={open}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.type}
        getOptionDisabled={(option) => option.type === 'label'}
        onOpen={autocompleteOpenHandler}
        onBlur={autocompleteBlurHandler}
        onFocus={autocompleteFocusHandler}
        disabled={disabled}
        inputValue={autocompleteGetInputValue()}
        onInputChange={autocompleteInputChangeHandler}
        slotProps={{
          paper: {
            component: GroupFilter,
            sx: { width: dropdownWidth },
            ...customPaperProps,
          },
          popper: { sx: { width: dropdownWidth }, container: popperContainer },
          listbox: {
            component: ListBox,
            ...customListboxProps,
          },
        }}
        renderOption={(props, option) => {
          const handleClick = () => {
            if (option.type !== 'label') {
              autocompleteClouseHandler();
            }
          };

          return (
            <li {...props} onClick={handleClick} key={option.id}>
              <SearchOption
                option={option}
                loadPhoto={loadPhoto}
                loadImage={loadImage}
                filter={filter}
                onClose={autocompleteClouseHandler}
              />
            </li>
          );
        }}
        options={showResult && status !== 'loading' ? items : []}
        loading={status === 'loading'}
        loadingText={
          <Box display="flex" justifyContent="center" p="16">
            <CircularProgress size={30} />
          </Box>
        }
        noOptionsText={
          <>
            {showResult && status === 'complete' && (
              <Box
                height={56}
                py="32"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="body2" color="text.light.secondary">
                  Ничего не найдено
                </Typography>
              </Box>
            )}
            {showResult && status === 'failed' && (
              <Box
                height={56}
                py="32"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="body2" color="text.light.secondary">
                  {getErrorText(error)}
                </Typography>
              </Box>
            )}
          </>
        }
        disableClearable
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            classes={{
              root: `${classes.input} aqa_select`,
            }}
            variant="outlined"
            placeholder={getPlaceholder()}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <Icon
                    name="search_outline"
                    color="text.light.tertirary"
                    size="20"
                  />
                ),
                endAdornment: (
                  <>
                    {!!inputValue && (
                      <Box px="8" position="absolute" right={0}>
                        <IconButton
                          onClick={autocompleteResetHandler}
                          blurOnClick={false}
                        >
                          <Icon name="dismiss_overlay" size="24" />
                        </IconButton>
                      </Box>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );

  if (closedStateComponent) {
    return (
      <Box display="flex" justifyContent="space-between" flex={1}>
        {!open && closedStateComponent}
        {searchComponent}
      </Box>
    );
  }

  return searchComponent;
};
